// Types
import { ResizeBorderHorizontal } from '@src/components/common/ResizeBorder';
import DebugModal from '@src/components/debug/DebugModal';
import { useTimeline } from '@src/hooks/timeline/useTimeline';
import { useTimelineTracks } from '@src/hooks/timeline/useTimelineTracks';
import { useTimelineLayoutResize } from '@src/hooks/useTimelineLayoutResize';
import { useLayoutStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { usePinch } from '@use-gesture/react';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DraggableCore } from 'react-draggable';
import styled from 'styled-components';

import TimelineControls from './TimelineControls';
import { TimelineHeader } from './TimelineHeader';
import { TimelineTrackLabels } from './TimelineTrackLabels';
import { TimelineTracks } from './TimelineTracks';

import type { ElementState } from '@src/renderer/ElementState';
import type { Story } from '@src/types.ts/story';

type Props = {
  story?: Story;
};

const FALLBACK_MAX_WIDTH = 700;

export const Timeline: React.FC<Props> = observer(props => {
  const videoCreator = useVideoCreatorStore();
  const layoutStore = useLayoutStore();

  // Refs for DOM elements
  const scrollRef = useRef<HTMLDivElement>(null);
  const tracksDivRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Local state
  const [maxWidth, setMaxWidth] = useState(FALLBACK_MAX_WIDTH);
  const [offsetY, setOffsetY] = useState(0);
  const [refScrollLeft, setRefScrollLeft] = useState<number>(0);
  const [selectedElement, setSelectedElement] = useState<ElementState | null>(
    null,
  );
  const refScrollLeftRef = useRef<number>(0);

  // Calculate dimensions
  const duration = videoCreator.duration;

  // Main timeline functionality through compound hook
  const {
    animatedRef,
    timelineRef,
    scale,
    handlePinch,
    currentTime,
    handleKeyDown,
    handleKeyUp,
    handleTimelineClick,
    contextMenuState,
    showContextMenu,
    hideContextMenu,
  } = useTimeline({
    duration,
    maxWidth,
    frameRate: videoCreator.timelineStore.framerate,
    enableKeyboardShortcuts: true,
    autoScroll: true,
  });

  // Track management
  const { tracks, activeTracks, trackHeight, getTrackInfo } =
    useTimelineTracks();

  // Layout resize functionality (existing)
  const {
    throttledHandleMouseMove,
    handleMouseUp,
    handleMouseDown,
    isDragging,
  } = useTimelineLayoutResize();

  // Sync refs
  useEffect(() => {
    if (timelineRef.current !== ref.current) {
      (timelineRef as any).current = ref.current;
    }
  }, []);

  // Calculate track width
  let trackWidth: number | undefined = duration * scale + 14;
  if (trackWidth < maxWidth - 14) {
    trackWidth = undefined;
  }

  // Calculate extra time width for timeline header
  const timeDiff = maxWidth - duration * scale;
  const extraTimeWidth = Math.max(0, timeDiff);

  // Mouse event handlers
  const mouseEnter = useCallback(() => {
    const currentScroll = window.scrollY;
    scrollRef.current?.focus();
    window.scrollTo({ top: currentScroll });
  }, []);

  const mouseLeave = useCallback(() => {
    scrollRef.current?.blur();
  }, []);

  // Element click handler for debug modal
  const handleElementClick = useCallback((element: ElementState) => {
    setSelectedElement(element);
  }, []);

  // Context menu handlers
  const onContextMenu = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
      element: ElementState,
    ) => {
      showContextMenu(e, element);
    },
    [showContextMenu],
  );

  const handlePasteContextMenu = useCallback(
    async (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
      track: number,
      elements: ElementState[],
    ) => {
      const { action, copied } = videoCreator.timelineClipboard || {};
      if (!action || !copied) return;

      const element = elements[0];
      if (element.source.type !== copied.element.source.type) {
        return;
      }

      const indent = tracksDivRef.current!.getBoundingClientRect().left;
      const posX = e.clientX + ref.current!.scrollLeft - indent;
      const time = (posX - 14) / scale;
      const isTaken = elements.some(
        e => time >= e.localTime && time <= e.localTime + e.duration,
      );
      if (isTaken) return;

      e.preventDefault();
      videoCreator.timelineClipboard = {
        ...videoCreator.timelineClipboard!,
        copied: {
          data: { time, track },
          element: videoCreator.timelineClipboard!.tmp.element,
        },
        pos: { left: e.clientX, top: e.clientY },
        action: 'paste',
      };
    },
    [videoCreator, scale],
  );

  // Scroll handling
  const handleScroll = useCallback(() => {
    if (ref.current && animatedRef.current) {
      animatedRef.current.style.left = `-${ref.current.scrollLeft}px`;
      setOffsetY(ref.current.scrollTop);
    }
  }, []);

  // Timeline click handler
  const onTimelineClick = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      await handleTimelineClick(e, tracksDivRef);
    },
    [handleTimelineClick],
  );

  // Main click handler for clearing selections
  const onMainClick = useCallback(() => {
    hideContextMenu();
    if (videoCreator.selectedVolumeKeyPoint) {
      videoCreator.selectVolumeKeyPoint(null);
    }
  }, [hideContextMenu, videoCreator]);

  // Gesture binding - adapt the gesture state to match our hook interface
  const bind = usePinch(
    state => {
      const adaptedState = {
        offset: state.offset,
        scale: 1, // Use default scale since usePinch doesn't provide this
        velocity: Array.isArray(state.velocity) ? state.velocity[0] : 0,
        direction: state.direction,
        maxWidth,
        origin: state.origin,
        last: state.last,
      };
      handlePinch(adaptedState);
    },
    {
      preventDefault: false,
      scaleBounds: { min: 0.1, max: 10 },
      rubberband: true,
    },
  );

  // Effects for width calculation and scroll tracking
  useEffect(() => {
    if (!tracksDivRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      const width = tracksDivRef.current?.clientWidth ?? FALLBACK_MAX_WIDTH;
      setMaxWidth(width);
    });
    resizeObserver.observe(tracksDivRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (refScrollLeftRef.current !== ref.current?.scrollLeft) {
        refScrollLeftRef.current = ref.current?.scrollLeft || 0;
        setRefScrollLeft(ref.current?.scrollLeft || 0);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      if (ref.current) {
        ref.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <Main
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
      ref={scrollRef}
      style={{
        minHeight: layoutStore.timelineHeight,
        maxHeight: layoutStore.timelineHeight,
        height: layoutStore.timelineHeight,
      }}
      onClick={onMainClick}
    >
      <DraggableCore
        onDrag={e => {
          throttledHandleMouseMove(e as unknown as MouseEvent);
        }}
        onStop={handleMouseUp}
        onMouseDown={handleMouseDown}
      >
        <ResizeBorderHorizontal
          onMouseUp={handleMouseUp}
          isDragging={isDragging}
        />
      </DraggableCore>

      <TimelineControls />

      <Content {...bind()}>
        <TracksLeft>
          <Controls />
          <TimelineTrackLabels
            tracks={tracks}
            activeTracks={activeTracks}
            offsetY={offsetY}
            getTrackInfo={getTrackInfo}
          />
        </TracksLeft>

        <TracksRight ref={tracksDivRef} onClick={onTimelineClick}>
          <TracksRightInner>
            <TracksRightTop>
              <TracksRightTopInner ref={animatedRef}>
                <TimelineHeader
                  scale={scale}
                  duration={duration}
                  currentTime={currentTime}
                  maxWidth={maxWidth}
                  extraTimeWidth={extraTimeWidth}
                  refScrollLeft={refScrollLeft}
                  trackHeight={trackHeight}
                />
              </TracksRightTopInner>
            </TracksRightTop>

            <Tracks
              ref={ref}
              style={{ width: maxWidth - 14 }}
              onMouseDown={e => e.stopPropagation()}
            >
              <TimelineTracks
                tracks={tracks}
                activeTracks={activeTracks}
                scale={scale}
                trackWidth={trackWidth}
                onContextMenu={onContextMenu}
                onPasteContextMenu={handlePasteContextMenu}
                onElementClick={handleElementClick}
                contextMenuState={contextMenuState}
                story={props.story}
                getTrackInfo={getTrackInfo}
              />
            </Tracks>
          </TracksRightInner>
        </TracksRight>
      </Content>

      {selectedElement && (
        <DebugModal
          title="Timeline Element Debug"
          content={selectedElement}
          onClose={() => {
            setSelectedElement(null);
          }}
        />
      )}
    </Main>
  );
});

// Styled Components (keeping existing styles)
const Main = styled.div`
  position: relative;
  flex: 1 1 0%;
  background: #030419;
  border-top: 1px solid #333;
  display: flex;
  flex-direction: column;
  &:focus {
    outline: none;
  }
  z-index: 100000;
`;

const Content = styled.div`
  position: relative;
  display: flex;
  height: calc(100% - 45px);
  user-select: none;
  overflow: hidden;
  cursor: default;
`;

const TracksLeft = styled.div`
  width: 135px;
  position: relative;
`;

const Controls = styled.div`
  height: 35px;
  padding: 8px 5px 7px;
  display: flex;
  -webkit-box-align: center;
  align-items: center;
  box-sizing: border-box;
`;

const TracksRight = styled.div`
  position: relative;
  flex: 1 1 0%;
  height: 100%;
`;

const TracksRightInner = styled.div`
  position: relative;
  flex: 1 1 0%;
  height: 100%;
`;

const TracksRightTop = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const TracksRightTopInner = styled.div`
  position: absolute;
  height: 100%;
`;

const Tracks = styled.div`
  top: 35px;
  height: calc(100% - 35px);
  position: absolute;
  overflow: auto;
`;
